import { useMemo, useRef, useState } from 'react'
import {
  PICKER_SIDE_OFFSET,
  PickerPanel,
  pickerPopoverClassName,
  pickerTriggerOpenClassName,
} from './PickerPanel'
import type { PickerAction, PickerOption } from './PickerPanel'
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '@/components/ui/drawer'
import { useIsTouchPicker } from '@/hooks/useMobile'
import { cn } from '@/lib/utils'

export interface IconComboBoxOption {
  value: string
  label: string
  icon: ReactNode
  keywords?: Array<string>
  meta?: ReactNode
  group?: string
  /** Single key that sets this option; defaults to its 1–9 position with numeric shortcuts. */
  shortcut?: string
}

interface IconComboBoxProps {
  options: Array<IconComboBoxOption>
  selectedValue?: string | null
  onChange: (value: string) => void
  placeholderText?: string
  searchText?: string
  emptyStateLabel?: string
  isDisabled?: boolean
  buttonClassName?: string
  fontSize?: 'xs' | 'sm' | 'base'
  renderTriggerIcon?: (selectedOption?: IconComboBoxOption) => ReactNode
  buttonAriaLabel?: string
  isOpen?: boolean
  onOpenChange?: (open: boolean) => void
  triggerProps?: ButtonHTMLAttributes<HTMLButtonElement> & {
    [key: `data-${string}`]: string | undefined
  }
  /** @deprecated Not rendered: the trigger's tooltip carries the shortcut (design 31a). */
  searchShortcutHint?: string | Array<string> | null
  enableNumericShortcuts?: boolean
  action?: PickerAction
}

/**
 * Icon-trigger combobox over the shared PickerPanel shell.
 * Useful when a compact trigger with an icon is required.
 */
export function IconComboBox({
  options,
  selectedValue,
  onChange,
  placeholderText = 'Select option...',
  searchText = 'Search...',
  emptyStateLabel,
  isDisabled = false,
  buttonClassName,
  renderTriggerIcon,
  buttonAriaLabel,
  isOpen: controlledOpen,
  onOpenChange,
  triggerProps,
  enableNumericShortcuts = false,
  action,
}: IconComboBoxProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const isControlled = typeof controlledOpen === 'boolean'
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen
  const blockOpenRef = useRef(false)
  const pointerOnTriggerRef = useRef(false)
  const setIsOpen = (nextOpen: boolean) => {
    if (nextOpen && blockOpenRef.current) return
    if (!isControlled) {
      setUncontrolledOpen(nextOpen)
    }
    onOpenChange?.(nextOpen)
  }
  const closeAfterSelect = () => {
    blockOpenRef.current = true
    setIsOpen(false)
    setTimeout(() => {
      blockOpenRef.current = false
    }, 350)
  }

  const selectedOption = useMemo(
    () => options.find((option) => option.value === selectedValue) ?? null,
    [options, selectedValue],
  )
  const handleValueChange = (value: string) => {
    onChange(value)
    closeAfterSelect()
  }

  const pickerOptions = useMemo<Array<PickerOption>>(
    () =>
      options.map((option, index) => ({
        value: option.value,
        label: option.label,
        icon: option.icon,
        meta: option.meta,
        group: option.group,
        keywords: option.keywords,
        shortcut:
          option.shortcut ??
          (enableNumericShortcuts && index < 9 ? String(index + 1) : undefined),
      })),
    [options, enableNumericShortcuts],
  )

  const isMobile = useIsTouchPicker()

  const triggerElement = (
    <button
      type="button"
      className={cn(
        'flex h-6 w-6 items-center justify-center rounded transition-all duration-200',
        'hover:bg-accent focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1',
        'disabled:opacity-50',
        pickerTriggerOpenClassName,
        buttonClassName,
      )}
      disabled={isDisabled}
      aria-label={buttonAriaLabel ?? placeholderText}
      {...triggerProps}
      onPointerDown={(event) => {
        triggerProps?.onPointerDown?.(event)
        pointerOnTriggerRef.current = true
      }}
      onClick={(event) => {
        triggerProps?.onClick?.(event)
        if (!isMobile) return
        if (!pointerOnTriggerRef.current) return
        pointerOnTriggerRef.current = false
        setIsOpen(true)
      }}
    >
      {renderTriggerIcon
        ? renderTriggerIcon(selectedOption ?? undefined)
        : selectedOption?.icon}
    </button>
  )

  const commandContent = (
    <PickerPanel
      options={pickerOptions}
      selected={selectedValue}
      onSelect={handleValueChange}
      searchPlaceholder={searchText}
      emptyText={emptyStateLabel}
      action={
        action
          ? {
              ...action,
              onSelect: () => {
                closeAfterSelect()
                action.onSelect()
              },
            }
          : undefined
      }
    />
  )

  if (isMobile) {
    // See ComboBox: DrawerTrigger plus a controlled open loops on a phone tap.
    return (
      <>
        {triggerElement}
        <Drawer open={isOpen} onOpenChange={setIsOpen} direction="bottom" repositionInputs={false} handleOnly>
          <DrawerContent onOpenAutoFocus={(event) => event.preventDefault()}>
            <DrawerTitle className="sr-only">Select an option</DrawerTitle>
            <DrawerDescription className="sr-only">
              Choose an option from the list.
            </DrawerDescription>
            <div className="max-h-[60vh] overflow-y-auto">{commandContent}</div>
          </DrawerContent>
        </Drawer>
      </>
    )
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        {triggerElement}
      </PopoverTrigger>
      <PopoverContent
        className={pickerPopoverClassName}
        align="start"
        sideOffset={PICKER_SIDE_OFFSET}
      >
        {commandContent}
      </PopoverContent>
    </Popover>
  )
}
